/* eslint-disable @typescript-eslint/ban-ts-comment */
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { StarIcon } from '@heroicons/react/20/solid'
import { Link } from 'gatsby'
import intersection from 'lodash/intersection'
import isNil from 'lodash/isNil'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Highlight } from 'react-instantsearch-dom'
import tw from 'tailwind.macro'

import { CheckIcon } from '@components/common/GuaranteedIcon'

import { theme } from '@src/../tailwind.config'
import { AlgoliaCatalogCourseType } from '@src/interfaces/AlgoliaResultCourseType'
import AnalyticsClient from '@src/utils/AnalyticsClient'
import { isCourseNew } from '@src/utils/safireUtils'

import { getHitCategory } from './SearchCatalogResultsHits'

const colors = theme.colors

type HitWithSessionsProps = {
  hit: AlgoliaCatalogCourseType
  cityFilter: string | null
  index?: number
}
type LinkProps = {
  to: string
}

const ResultItemInner = styled.div`
  ${tw`flex justify-between items-start p-3`};
`
export const ClickableTitle = styled(Link)<LinkProps>`
  ${tw`text-textColor`};
`
export const Title = styled.h3`
  ${tw`text-sm leading-tight font-bold`};
`
export const DescWrapper = styled.div`
  ${tw`text-xs italic leading-tight mt-2 opacity-75`};
  & > .ais-Highlight:empty::after {
    content: '\\00a0';
  }
`

const HitWithSessions = ({ hit, cityFilter, index }: HitWithSessionsProps) => {
  const { t } = useTranslation()

  const descNode = isNil(hit.MetaDescription) ? (
    <Highlight attribute='Description' hit={hit} />
  ) : (
    <Highlight attribute='MetaDescription' hit={hit} />
  )

  const junction = !isNil(cityFilter)
    ? intersection(hit.CitiesConfirmed, [cityFilter])
    : hit.CitiesConfirmed

  const sessions =
    junction.length > 0 ? (
      <div className='text-xs flex items-center mt-1'>
        Sessions <CheckIcon className='ml-1 text-pink-500 flex-shrink-0 w-3 h-3' />
      </div>
    ) : null

  const itemPrimeCSS = css({
    outline: `1px solid ${colors.gray[`500`]}`,
    outlineOffset: `-1px`,
  })

  const isNew = isCourseNew(hit)

  return (
    <ClickableTitle
      to={hit.url}
      className='clickable-title'
      onClick={() =>
        AnalyticsClient.track(`select_item`, {
          ecommerce: {
            items: [
              {
                item_name: hit.Name,
                item_id: hit.Id,
                // @ts-ignore
                item_brand: hit.Editors?.lvl0 || '',
                // @ts-ignore
                ...getHitCategory(hit.Themes),
                ...(typeof index === `number`
                  ? {
                      index,
                    }
                  : {}),
              },
            ],
          },
        })
      }
    >
      <ResultItemInner className='hit-item' css={hit.EditorialChoice && itemPrimeCSS}>
        <div>
          <div className='flex flex-col md3:flex-row items-start md3:items-center gap-2 mb-1'>
            <Title>
              <Highlight attribute='Name' hit={hit} />
            </Title>
            {isNew && (
              <span className='text-sm flex flex-shrink-0 text-revamp-red-500 items-center'>
                <StarIcon className='w-3 h-3' style={{ marginBottom: '2px', marginRight: '4px' }} />
                {t('revamp:new')}
              </span>
            )}
          </div>
          <DescWrapper>{descNode}</DescWrapper>
        </div>
        <div className='ml-3 flex-shrink-0 flex flex-col items-end whitespace-no-wrap'>
          <h4 className='text-sm leading-tight font-bold'>
            {t(`search:durationInDays`, { count: hit.DurationInDays })}
          </h4>
          {sessions}
        </div>
      </ResultItemInner>
    </ClickableTitle>
  )
}

export default HitWithSessions
